import { useEffect, useState } from 'react';
import './App.css';
import OverviewPage from './pages/OverviewPage';
import ChatPage from './pages/ChatPage';
import SideBetsPage from './pages/SideBetsPage';
import BonusesPage from './pages/BonusesPage';
import SocialPage from './pages/SocialPage';
import Home1Page from './pages/Home1Page';
import { initializeAllCharts } from './scripts/initAll';
import { initializeAnalytics, trackPageView } from './utils/googleAnalytics';

const PAGES = [
  { key: 'overview', label: 'Overview' },
  { key: 'chat', label: 'Chat WBG' },
  { key: 'sideBets', label: 'Side Bets' },
  { key: 'bonuses', label: 'Bonuses & Table Games' },
  { key: 'social', label: 'Community & Fun' },
];

const CURRENT_YEAR = new Date().getFullYear();
const PREVIOUS_YEAR = CURRENT_YEAR - 1;
const YEAR_FILTERS = [
  { key: 'current', label: `${CURRENT_YEAR}` },
  { key: 'previous', label: `${PREVIOUS_YEAR}` },
  { key: 'all', label: 'All time' },
];
const HOME1_PREVIEW_PASSWORD = 'preview';
const HOME1_AUTH_STORAGE_KEY = 'vegasMattHome1PreviewAccess';

function hasStoredHome1Access() {
  if (typeof window === 'undefined') return false;

  try {
    return window.sessionStorage.getItem(HOME1_AUTH_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function storeHome1Access() {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(HOME1_AUTH_STORAGE_KEY, 'true');
  } catch {
    // Password entry still works if sessionStorage is unavailable.
  }
}

function Home1PasswordGate({ children }) {
  const [hasAccess, setHasAccess] = useState(hasStoredHome1Access);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();

    if (password === HOME1_PREVIEW_PASSWORD) {
      storeHome1Access();
      setHasAccess(true);
      setPassword('');
      setError('');
      return;
    }

    setError('Incorrect password.');
  };

  if (hasAccess) {
    return children;
  }

  return (
    <main className="home1-password-page">
      <form className="home1-password-card" onSubmit={handleSubmit}>
        <p className="home1-password-kicker">Preview access</p>
        <h1>Home 1 is password protected</h1>
        <label htmlFor="home1-password">Password</label>
        <input
          id="home1-password"
          type="password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
            setError('');
          }}
          autoComplete="current-password"
          autoFocus
        />
        {error ? <p className="home1-password-error">{error}</p> : null}
        <button type="submit">Enter</button>
      </form>
    </main>
  );
}

function App() {
  const [activePage, setActivePage] = useState('overview');
  const [isNavCollapsed] = useState(false);
  const [yearFilter, setYearFilter] = useState('current');
  const isHome1 =
    typeof window !== 'undefined' && (window.location.pathname === '/home1' || window.location.hash === '#/home1');

  const selectedYear =
    yearFilter === 'all' ? null : yearFilter === 'previous' ? PREVIOUS_YEAR : CURRENT_YEAR;

  useEffect(() => {
    if (process.env.NODE_ENV !== 'test') {
      initializeAllCharts();
    }
  }, []);

  useEffect(() => {
    initializeAnalytics();
  }, []);

  useEffect(() => {
    if (isHome1) {
      return;
    }

    trackPageView(`/${activePage}`);
  }, [activePage, isHome1]);

  if (isHome1) {
    return (
      <Home1PasswordGate>
        <Home1Page />
      </Home1PasswordGate>
    );
  }


  return (
    <div className="App">
      <header className={`app-header ${isNavCollapsed ? 'collapsed' : ''}`}>
        <h1 className="app-title">OFFICIAL VEGAS MATT STATS</h1>
        <p className="app-subtitle">
          Dive into the stats, charts, and community moments from the Vegas Matt universe. Use the buttons below to explore
          different sections.
        </p>
        <nav className="app-navigation">
          {PAGES.map((page) => (
            <button
              key={page.key}
              type="button"
              className={`nav-button ${activePage === page.key ? 'active' : ''}`}
              onClick={() => setActivePage(page.key)}
            >
              {page.label}
            </button>
          ))}
        </nav>
        <div className="year-filter" role="group" aria-label="Date selector">
          <span className="year-filter__label">Date selector</span>
          <div className="year-filter__buttons">
            {YEAR_FILTERS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`year-filter__button ${yearFilter === option.key ? 'active' : ''}`}
                onClick={() => setYearFilter(option.key)}
                aria-pressed={yearFilter === option.key}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="app-content">
        <OverviewPage
          isActive={activePage === 'overview'}
          selectedYear={selectedYear}
          currentYear={CURRENT_YEAR}
          previousYear={PREVIOUS_YEAR}
          onYearChange={setYearFilter}
        />
        <ChatPage isActive={activePage === 'chat'} />
        <SideBetsPage isActive={activePage === 'sideBets'} selectedYear={selectedYear} />
        <BonusesPage isActive={activePage === 'bonuses'} selectedYear={selectedYear} />
        <SocialPage isActive={activePage === 'social'} selectedYear={selectedYear} />
      </main>
    </div>
  );
}

export default App;
